import { Link } from "react-router-dom";
import Shell from "../components/Shell";
import { useAufgaben, useGesehen } from "../hooks/useAufgaben";
import type { Aufgabe } from "../hooks/useAufgaben";
import { useVertraege, useKandidaten, eur } from "../hooks/useVertraege";

const STUFEN: { key: Aufgabe["stufe"]; titel: string; leer?: string }[] = [
  { key: "neu", titel: "Neu abgeschlossen" },
  { key: "jetzt", titel: "Jetzt dran", leer: "Nichts Dringendes." },
  { key: "bald", titel: "Demnächst" },
  { key: "warten", titel: "Worauf wir warten" },
];

/**
 * Aufgaben — der Schreibtisch der Pflege (vorher „Heute").
 * Wie in Kontor: oben was zu tun ist, jede Zeile mit dem Knopf, der es erledigt.
 * Die Kennzahlen sind Beiwerk und stehen klein darunter.
 */
export default function AufgabenPage() {
  const { liste, laedt } = useAufgaben();
  const { markieren } = useGesehen();
  const { data: vertraege = [] } = useVertraege();
  const { data: kandidaten = [] } = useKandidaten();
  const aktiv = vertraege.filter(v => v.status === "aktiv");
  const jahr = aktiv.reduce((s, v) => s + Number(v.jahres_netto || 0), 0);
  const jetzt = liste.filter(a => a.stufe === "jetzt").length;
  const neu = liste.filter(a => a.stufe === "neu").length;

  return (
    <Shell title="Aufgaben"
      subtitle={laedt ? "Lade…" : grossAnfang([neu && `${neu} neu abgeschlossen`, jetzt ? `${jetzt} jetzt dran` : "nichts Dringendes"]
        .filter(Boolean).join(" · "))}
      action={<Link className="pf-btn" to="/vertraege/neu">＋ Neuer Vertrag</Link>}>
      {STUFEN.map(st => {
        const zeilen = liste.filter(a => a.stufe === st.key);
        if (!zeilen.length && !st.leer) return null;
        return (
          <section key={st.key} className="pf-gruppe">
            <h2 className="pf-gruppe-kopf">
              <span className={"pf-punkt is-" + ({ neu: "gruen", jetzt: "rot", bald: "grau", warten: "blau" } as const)[st.key]} />
              {st.titel}
              {zeilen.length > 0 && <span className="pf-gruppe-zahl">{zeilen.length}</span>}
            </h2>
            {zeilen.length === 0 ? (
              <div className="pf-leer">{laedt ? "Lade…" : st.leer}</div>
            ) : (
              <div className="pf-aufgaben">
                {zeilen.map(a => (
                  <div key={a.key} className={"pf-aufgabe is-" + a.ton}>
                    <div className="pf-aufgabe-text">
                      <div className="pf-aufgabe-titel">{a.titel}</div>
                      <div className="pf-aufgabe-grund">{a.grund}</div>
                    </div>
                    {a.gesehenId && (
                      <button type="button" className="pf-linkbtn" onClick={() => markieren(a.gesehenId!)}>Gesehen</button>
                    )}
                    <Link className="pf-btn is-ghost is-small" to={a.ziel}
                      onClick={() => { if (a.gesehenId) markieren(a.gesehenId); }}>{a.knopf} →</Link>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <dl className="pf-zahlen">
        <div className="pf-zahl"><dt>Aktive Verträge</dt><dd>{aktiv.length}</dd></div>
        <div className="pf-zahl"><dt>Jahreswert netto</dt><dd>{eur(jahr)}</dd></div>
        <div className="pf-zahl"><dt>Angebote offen</dt><dd>{vertraege.filter(v => v.status === "angeboten").length}</dd></div>
        <div className="pf-zahl">
          <dt>Interessenten</dt>
          <dd><Link to="/kunden?filter=interessent">{kandidaten.filter(k => !k.hat_vertrag).length}</Link></dd>
        </div>
      </dl>
    </Shell>
  );
}

/** „nichts Dringendes" steht mal vorn, mal hinter „2 neu abgeschlossen". */
function grossAnfang(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}
